import { Box, Button, Grid, Typography } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { TektonPipelineCards, TektonRefreshProvider, CustomWorkflowCards } from '../tekton';
import { isTektonCIAvailable } from '@backstage-community/plugin-tekton';
import { TemplateTasksCard } from '../scaffolder';
import { EmptyState, InfoCard } from '@backstage/core-components';
import {
  EntityGitlabContent,
  EntityGitlabMergeRequestsTable,
  isGitlabAvailable,
} from '@immobiliarelabs/backstage-plugin-gitlab';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

// Kubernetes Enhanced Plugin (Lens-style)
import {
  EntityKubernetesEnhancedContent,
  isKubernetesEnhancedAvailable,
} from '@internal/plugin-kubernetes-enhanced';
import {
  EntityArgoCDHistoryCard,
  EntityArgoCDOverviewCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';

// Knative Serving Plugin
import {
  isKnativeServiceAvailable,
} from '@internal/plugin-knative-serving';

// Kubernetes Workloads Plugin (K8s 原生管理)
import {
  SystemEnvironmentsContent as K8sEnvironmentsContent,
  SystemWorkflowsContent as K8sWorkflowsContent,
  WorkloadRevisionContent,
  isK8sManagedSystem,
} from '@internal/plugin-kubernetes-workloads';
import { KnativeServingWithPipelines, KnativeWorkflowsContent } from '../knative';

// Grafana 组件（监控 tab 暂时隐藏）
// import {
//   EntityGrafanaDashboardsCard,
//   EntityGrafanaAlertsCard,
//   EntityOverviewDashboardViewer,
//   isDashboardSelectorAvailable,
//   isAlertSelectorAvailable,
//   isOverviewDashboardAvailable,
// } from '@k-phoen/backstage-plugin-grafana';

// 增强的 ArgoCD 组件（已注释，暂不使用）
// import { EnhancedDeploymentSummary } from '../argocd-enhanced';

// GitLab Pipeline Runs 组件
import { GitLabPipelineRuns } from '../gitlab';

// Tools 组件
import { ToolsContent, SystemToolsContent } from '../tools';

// System 组件
import {
  SystemOverviewContent,
  SystemDeploymentsSummary,
} from '../system';

// Jira Dashboard（Axis）- 用于查看 Jira 问题列表
import {
  EntityJiraDashboardContent,
  isJiraDashboardAvailable,
} from '@axis-backstage/plugin-jira-dashboard';

import { isTestReportsAvailable } from './entityConditions';
import { GroupMembersCard } from './GroupMembersCard';

const techdocsContent = (
  <div style={{ margin: '-24px' }}>
    <EntityTechdocsContent withSearch={false}>
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </EntityTechdocsContent>
  </div>
);

const cicdContent = (
  // CI 内容 - GitLab Pipeline Runs
  <Grid container spacing={3}>
    <EntitySwitch>
      <EntitySwitch.Case if={isGitlabAvailable}>
        <Grid item xs={12}>
          <GitLabPipelineRuns />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* 默认空状态 - 当没有 GitLab 可用时 */}
    <EntitySwitch>
      <EntitySwitch.Case if={e => !isGitlabAvailable(e)}>
        <Grid item xs={12}>
          <EmptyState
            title="No CI available for this entity"
            missing="info"
            description="You need to add GitLab annotation to your component if you want to enable CI for it."
            action={
              <Button
                variant="contained"
                color="primary"
                href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
              >
                Read more
              </Button>
            }
          />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

const ReportsContent = () => {
  const { entity } = useEntity();
  const links = (entity?.metadata?.links ?? []) as { title?: string; url?: string }[];
  const reportsLink = links.find(link =>
    link?.title?.toLowerCase().includes('report') || link?.title?.includes('报告'),
  );
  const baseUrl = reportsLink?.url?.replace(/\/$/, '');

  if (!baseUrl) {
    return (
      <EmptyState
        title="缺少报告入口"
        description="请在 catalog-info.yaml 的 metadata.links 中添加指向 GitLab Pages 报告根目录的链接,例如 https://<pages-host>/invoice"
        missing="info"
      />
    );
  }

  const ReportLinkCard = ({ title, path, description }: { title: string; path: string; description: string }) => (
    <InfoCard title={title} divider>
      <Box p={2}>
        <Typography variant="body1" paragraph>
          {description}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          href={`${baseUrl}${path}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          打开 {title}
        </Button>
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            链接: {baseUrl}{path}
          </Typography>
        </Box>
      </Box>
    </InfoCard>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <ReportLinkCard
          title="单测 Allure"
          path="/allure/index.html"
          description="查看单元测试的 Allure 报告,包含测试用例执行情况、通过率等详细信息。"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ReportLinkCard
          title="接口自动化 Allure"
          path="/api-autotest/index.html"
          description="查看接口自动化测试的 Allure 报告,包含 API 测试结果和响应数据。"
        />
      </Grid>
      <Grid item xs={12}>
        <ReportLinkCard
          title="Jacoco 覆盖率"
          path="/coverage/index.html"
          description="查看代码覆盖率报告,了解测试覆盖的代码范围和质量指标。"
        />
      </Grid>
    </Grid>
  );
};

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={280} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>

    {/* Argo CD Cards */}
    <EntitySwitch>
      <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
        {/* 临时注释掉增强版组件
        <Grid item sm={6}>
          <EnhancedDeploymentSummary />
        </Grid>
        */}
        <Grid item sm={12}>
          <EntityArgoCDOverviewCard />
        </Grid>
        <Grid item sm={12}>
          <EntityArgoCDHistoryCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="Gitlab CI">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/reports"
      title="测试报告"
      if={isTestReportsAvailable}
    >
      <ReportsContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/tekton"
      title="CD Pipelines"
      if={isTektonCIAvailable}
    >
      <TektonRefreshProvider>
        <Grid container spacing={3}>
          {/* Pipeline 卡片展示区域（包含 Pipeline Runs 表格） */}
          <Grid item xs={12}>
            <TektonPipelineCards />
          </Grid>
        </Grid>
      </TektonRefreshProvider>
    </EntityLayout.Route>

    {/* 自定义工作流 Tab - 从 ArgoCD 部署的目标集群/命名空间获取 Pipeline */}
    <EntityLayout.Route
      path="/custom-workflows"
      title="自定义工作流"
      if={isArgocdAvailable}
    >
      <TektonRefreshProvider>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomWorkflowCards />
          </Grid>
        </Grid>
      </TektonRefreshProvider>
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/gitlab"
      title="GitLab">
      <EntityGitlabContent />
    </EntityLayout.Route>

    {/* 
    <EntityLayout.Route 
      if={isGitlabAvailable}
      path="/gitlab-pipelines" 
      title="Pipelines">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGitlabPipelinesTable />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    */}

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/merge-requests"
      title="MRs">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGitlabMergeRequestsTable />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* Jira Dashboard - 查看 Jira 问题列表 */}
    <EntityLayout.Route
      if={entity => isJiraDashboardAvailable(entity, 'jira.com')}
      path="/jira"
      title="Jira"
    >
      <EntityJiraDashboardContent />
    </EntityLayout.Route>

    {/* Tech Insights tab 暂时隐藏（路由代码保留）
    <EntityLayout.Route
      path="/tech-insights"
      title="Tech Insights"
      if={isTechInsightsAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityTechInsightsScorecardContent title="Tech Insights" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    */}

    {/* Kubernetes Enhanced - Lens 风格增强版 */}
    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesEnhancedAvailable}
    >
      <EntityKubernetesEnhancedContent refreshIntervalMs={30000} />
    </EntityLayout.Route>

    {/* Knative CD Pipelines - 发版 / 切流工作流（独立 tab，置于 Knative Serving 之前） */}
    <EntityLayout.Route
      path="/knative-cd"
      title="CD Pipelines"
      if={isKnativeServiceAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <KnativeWorkflowsContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* Knative Serving - Serverless 工作负载管理 */}
    <EntityLayout.Route
      path="/knative"
      title="Knative Serving"
      if={isKnativeServiceAvailable}
    >
      <KnativeServingWithPipelines />
    </EntityLayout.Route>

    {/* 监控 tab 暂时隐藏
    <EntityLayout.Route
      path="/monitoring"
      title="监控"
    >
      <Grid container spacing={3} alignItems="stretch">
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean(isOverviewDashboardAvailable(e))}>
            <Grid item xs={12} style={{ height: '800px' }}>
              <EntityOverviewDashboardViewer />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={e => Boolean(isDashboardSelectorAvailable(e))}>
            <Grid item md={6} xs={12}>
              <EntityGrafanaDashboardsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean(isAlertSelectorAvailable(e))}>
            <Grid item md={6} xs={12}>
              <EntityGrafanaAlertsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    */}

    {/* API tab 暂时隐藏
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    */}

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="ArgoCD"
      if={isArgocdAvailable}
    >
      <Grid container spacing={3} alignItems="stretch">
        {/* 临时注释掉增强版组件
        <Grid item md={6} xs={12}>
          <EnhancedDeploymentSummary />
        </Grid>
        */}
        <Grid item xs={12}>
          <EntityArgoCDOverviewCard />
        </Grid>

        {/* 历史记录 */}
        <Grid item xs={12}>
          <EntityArgoCDHistoryCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/tools" title="工具">
      <ToolsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="Gitlab CI">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/tekton"
      title="CD Pipelines"
      if={isTektonCIAvailable}
    >
      <TektonRefreshProvider>
        <Grid container spacing={3}>
          {/* Pipeline 卡片展示区域（包含 Pipeline Runs 表格） */}
          <Grid item xs={12}>
            <TektonPipelineCards />
          </Grid>
        </Grid>
      </TektonRefreshProvider>
    </EntityLayout.Route>

    {/* 自定义工作流 Tab - 从 ArgoCD 部署的目标集群/命名空间获取 Pipeline */}
    <EntityLayout.Route
      path="/custom-workflows"
      title="自定义工作流"
      if={isArgocdAvailable}
    >
      <TektonRefreshProvider>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomWorkflowCards />
          </Grid>
        </Grid>
      </TektonRefreshProvider>
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/gitlab"
      title="GitLab">
      <EntityGitlabContent />
    </EntityLayout.Route>

    {/*
    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/gitlab-pipelines"
      title="Pipelines">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGitlabPipelinesTable />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    */}

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/merge-requests"
      title="MRs">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGitlabMergeRequestsTable />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* Jira Dashboard - 查看 Jira 问题列表 */}
    <EntityLayout.Route
      if={entity => isJiraDashboardAvailable(entity, 'jira.com')}
      path="/jira"
      title="Jira"
    >
      <EntityJiraDashboardContent />
    </EntityLayout.Route>

    {/* Tech Insights tab 暂时隐藏（路由代码保留）
    <EntityLayout.Route
      path="/tech-insights"
      title="Tech Insights"
      if={isTechInsightsAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityTechInsightsScorecardContent title="Tech Insights" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    */}

    {/* Kubernetes Enhanced - Lens 风格增强版 */}
    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesEnhancedAvailable}
    >
      <EntityKubernetesEnhancedContent refreshIntervalMs={30000} />
    </EntityLayout.Route>

    {/* Knative CD Pipelines - 发版 / 切流工作流（独立 tab，置于 Knative Serving 之前） */}
    <EntityLayout.Route
      path="/knative-cd"
      title="CD Pipelines"
      if={isKnativeServiceAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <KnativeWorkflowsContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* Knative Serving - Serverless 工作负载管理 */}
    <EntityLayout.Route
      path="/knative"
      title="Knative Serving"
      if={isKnativeServiceAvailable}
    >
      <KnativeServingWithPipelines />
    </EntityLayout.Route>

    {/* 监控 tab 暂时隐藏
    <EntityLayout.Route
      path="/monitoring"
      title="监控"
    >
      <Grid container spacing={3} alignItems="stretch">
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean(isOverviewDashboardAvailable(e))}>
            <Grid item xs={12} style={{ height: '800px' }}>
              <EntityOverviewDashboardViewer />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={e => Boolean(isDashboardSelectorAvailable(e))}>
            <Grid item md={6} xs={12}>
              <EntityGrafanaDashboardsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean(isAlertSelectorAvailable(e))}>
            <Grid item md={6} xs={12}>
              <EntityGrafanaAlertsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    */}

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="ArgoCD"
      if={isArgocdAvailable}
    >
      <Grid container spacing={3} alignItems="stretch">
        {/* 临时注释掉增强版组件
        <Grid item md={6} xs={12}>
          <EnhancedDeploymentSummary />
        </Grid>
        */}
        <Grid item xs={12}>
          <EntityArgoCDOverviewCard />
        </Grid>

        {/* 历史记录 */}
        <Grid item xs={12}>
          <EntityArgoCDHistoryCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/tools" title="工具">
      <ToolsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>


  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/tekton"
      title="CD Pipelines"
      if={isTektonCIAvailable}
    >
      <TektonRefreshProvider>
        <Grid container spacing={3}>
          {/* Pipeline 卡片展示区域（包含 Pipeline Runs 表格） */}
          <Grid item xs={12}>
            <TektonPipelineCards />
          </Grid>
        </Grid>
      </TektonRefreshProvider>
    </EntityLayout.Route>

    {/* 自定义工作流 Tab - 从 ArgoCD 部署的目标集群/命名空间获取 Pipeline */}
    <EntityLayout.Route
      path="/custom-workflows"
      title="自定义工作流"
      if={isArgocdAvailable}
    >
      <TektonRefreshProvider>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomWorkflowCards />
          </Grid>
        </Grid>
      </TektonRefreshProvider>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <GroupMembersCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    {/* 1. Overview - 系统概览 */}
    <EntityLayout.Route path="/overview" title="Overview">
      <SystemOverviewContent />
    </EntityLayout.Route>

    {/* 2. CD Pipelines - K8s 工作流（hose.io/powered-by: kubernetes） */}
    <EntityLayout.Route
      path="/cd-pipelines"
      title="CD Pipelines"
      if={isK8sManagedSystem}
    >
      <K8sWorkflowsContent />
    </EntityLayout.Route>

    {/* 3. Environment - K8s 环境（默认首页 path=/） */}
    <EntityLayout.Route
      path="/"
      title="Environment"
      if={isK8sManagedSystem}
    >
      <K8sEnvironmentsContent />
    </EntityLayout.Route>

    {/* 4. Infrastructure - 基础设施 + 工具集 */}
    <EntityLayout.Route
      path="/infrastructure"
      title="Infrastructure"
      if={isK8sManagedSystem}
    >
      <SystemToolsContent />
    </EntityLayout.Route>


    {/* 5. Components - 组件列表（所有 System 类型通用） */}
    <EntityLayout.Route path="/components" title="Components">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SystemDeploymentsSummary />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* 6. Diagram - 系统架构图 */}
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>

    {/* 7. Dependencies - 依赖关系 */}
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            height={400}
            title="Resource Dependencies"
            relations={[
              RELATION_DEPENDS_ON,
              RELATION_DEPENDENCY_OF,
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* 8. Docs - 系统文档 */}
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const templatePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={12}>
          <TemplateTasksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ── Workload Kind Page ──

const workloadPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/revisions" title="版本">
      <WorkloadRevisionContent />
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('template')} children={templatePage} />
    <EntitySwitch.Case if={isKind('workload')} children={workloadPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

